import React, { useMemo } from 'react';
import type ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';
import type { PublicScanBrandShare } from '@brand24-original/llm-types';
import ReportSection from './ReportSection';
import PieChart from '../chart/pie-chart/PieChart';
import { SOV_PIE_CHART_PALETTE } from '../../utils/sovPalette';
import { OTHERS_COLOR } from '../../utils/utils';
import { brandColorOf } from './brandColors';
import './ReportShareOfVoice.scss';
import { brandLabel } from './brandLabel';

interface ReportShareOfVoiceProps {
  brands: PublicScanBrandShare[];
  /** Wspólna dla całego raportu mapa kolorów marek. */
  brandColors: Map<string, string>;
  /** Raport zrzuca z tej instancji obraz wykresu do PDF-a. */
  chartRef?: React.RefObject<ReactECharts>;
}

/** Ile marek pokazuje ranking — dalsze lądują w „Others", żeby koło zostało czytelne. */
const TOP_BRANDS = 7;

export const ReportShareOfVoice: React.FC<ReportShareOfVoiceProps> = ({
  brands,
  brandColors,
  chartRef,
}) => {
  const { t } = useTranslation();

  const tracked = brands.find((entry) => entry.isTracked);
  const trackedLabel = tracked ? brandLabel(tracked.brand) : undefined;
  const othersLabel = t('others');

  const slices = useMemo(() => {
    const ranked = [...brands].sort((a, b) => b.shareOfVoice - a.shareOfVoice);
    const top = ranked.slice(0, TOP_BRANDS);

    // Badana marka jest w rankingu zawsze — nawet gdy wypadła poza czołówkę, to o nią chodzi.
    const trackedEntry = ranked.find((entry) => entry.isTracked);
    if (trackedEntry && !top.includes(trackedEntry)) {
      top.push(trackedEntry);
    }

    const entries = top.map((entry) => ({
      name: brandLabel(entry.brand),
      value: entry.shareOfVoice,
      isTracked: entry.isTracked,
    }));

    // Wszystko poza czołówką — i dalsze marki, i reszta rynku. Dwa miejsca po przecinku, tak jak
    // `getPieChartWithOthers` na dashboardzie V2, żeby ta sama marka nie miała dwóch różnych liczb.
    const covered = top.reduce((sum, entry) => sum + entry.shareOfVoice, 0);
    const rest = parseFloat((100 - covered).toFixed(2));
    if (rest > 0.5) {
      entries.push({ name: othersLabel, value: rest, isTracked: false });
    }

    return entries;
  }, [brands, othersLabel]);

  /** Zdanie o pozycji badanej marki — inne, gdy prowadzi, inne, gdy goni lidera. */
  const summary = useMemo(() => {
    if (!tracked) {
      return undefined;
    }

    const ranked = [...brands].sort((a, b) => b.shareOfVoice - a.shareOfVoice);
    const isLeader = ranked[0]?.brand === tracked.brand;
    // Liderowi mierzymy przewagę nad wiceliderem, reszcie — dystans do lidera.
    const rival = isLeader ? ranked[1] : ranked[0];

    if (!rival) {
      return t('reportSovOnly', { brand: trackedLabel, share: tracked.shareOfVoice });
    }

    const gap = Math.round(Math.abs(tracked.shareOfVoice - rival.shareOfVoice) * 10) / 10;
    return t(isLeader ? 'reportSovLeads' : 'reportSovTrails', {
      brand: trackedLabel,
      share: tracked.shareOfVoice,
      rival: brandLabel(rival.brand),
      gap,
    });
  }, [brands, tracked, trackedLabel, t]);

  const colorOf = (name: string, index: number): string =>
    name === othersLabel ? OTHERS_COLOR : brandColorOf(brandColors, name, name === trackedLabel, index);

  // Slajd bez danych w ogóle nie wchodzi do raportu — ani na stronie, ani w PDF.
  if (slices.length === 0) {
    return null;
  }

  return (
    <ReportSection
      title={t('reportSovTitle')}
      titleAccent={t('reportSovTitleAccent')}
      subtitle={summary}
      aside={
        <div className="report-section__panel">
          <h3 className="report-section__panel-title">{t('reportSovChartTitle')}</h3>
          <p className="report-section__panel-desc">{t('reportSovChartDesc')}</p>
          <PieChart
            chartData={slices}
            colorPalette={slices.map((slice, index) => colorOf(slice.name, index))}
            selectedBrand={trackedLabel}
            showLabels
            chartRef={chartRef}
          />
        </div>
      }
    >
      <ul className="report-sov__ranking">
        {slices.map((slice, index) => (
          <li
            key={slice.name}
            className={`report-sov__rank-item${
              slice.isTracked ? ' report-sov__rank-item--tracked' : ''
            }`}
          >
            <span className="report-sov__dot" style={{ backgroundColor: colorOf(slice.name, index) }} />
            <span className="report-sov__rank-label">{slice.name}</span>
            <span className="report-sov__rank-value">{slice.value}%</span>
          </li>
        ))}
      </ul>
    </ReportSection>
  );
};

export default ReportShareOfVoice;
